import type { ChangeEvent, FC } from "react";
import { useEffect, useMemo } from "react";

import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { objectKeys } from "app/helpers/objects";
import { Header } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/Header";
import { UploadEnrichmentInstructions } from "app/pages/sales-intelligence/modules/enrichment/components/UploadEnrichmentInstructions/UploadEnrichmentInstructions";
import { CSV_LIMIT_VALUE, HINT_WIDTH } from "app/pages/sales-intelligence/modules/enrichment/constants/constants";
import { CustomHint } from "app/pages/sales-intelligence/modules/enrichment/containers/UploadCSVEnrichmentContainer/CustomHint";
import {
    selectHasParsingError,
    selectIsParsing,
    selectParsingError,
} from "app/pages/sales-intelligence/modules/enrichment/store/selectors/selectors";
import type { EnrichmentType } from "app/pages/sales-intelligence/modules/enrichment/types/types";
import {
    CLOSE,
    ENR_COL_NUMBER_ERROR,
    ENR_CONTACTS_EMPTY_COLUMNS_ERROR,
    ENR_CONTACTS_MODAL_TITLE,
    ENR_CONTACTS_NO_FIELDS_ERROR,
    ENR_CONTACTS_OVER_LIMIT_ERROR,
    ENR_CONTACTS_QUOTA_ERROR,
    ENR_FORMAT_ERROR,
    ENR_MODAL_TITLE,
    ENR_NOT_A_DOMAINS_ERROR,
    ENR_OVER_LIMIT_ERROR,
} from "app/pages/sales-intelligence/modules/enrichment/utils/i18n/modal.texts";
import { useContactsQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useContactsQuota";
import { useExportQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useExportQuota";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import { MAX_CONTACTS_ROW_NUMBER } from "app/pages/sales-intelligence/sub-modules/management/components/constants";
import { UploadContactsCSV } from "app/pages/sales-intelligence/sub-modules/management/components/LoadList/components/UploadContactsCSV";
import { UploadCSV } from "app/pages/sales-intelligence/sub-modules/management/components/LoadList/components/UploadCSV";
import { formatNumberWithComa } from "app/pages/sales-intelligence/utils/leads";

import { StyledUploadContainer } from "./styles";

type UploadCSVEnrichmentContainerProps = {
    onClose?: () => void;
    onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
    type?: EnrichmentType;
};

const UploadCSVEnrichmentContainer: FC<UploadCSVEnrichmentContainerProps> = ({
    onClose,
    onFileChange,
    type = "company",
}) => {
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const exportQuota = useExportQuota(type === "company");
    const contactsQuota = useContactsQuota(type === "contact");

    const formattedQuota = formatNumberWithComa(
        type === "company" ? exportQuota.data.remaining : contactsQuota.data.remaining,
    );
    const isParsing = useSelector(selectIsParsing);
    const parsingError = useSelector(selectParsingError);
    const hasParsingError = useSelector(selectHasParsingError);

    useEffect(() => {
        if (hasParsingError) {
            const errorLabels = objectKeys(parsingError).find((label) => parsingError[label]);
            enrichmentTrackingService.csv.trackFileParseError(errorLabels, type);
        }
    }, [hasParsingError, parsingError]);

    const errorTexts = useMemo(() => {
        if (type === "company") {
            return {
                isOverTheLimit: translate(ENR_OVER_LIMIT_ERROR, { limit: CSV_LIMIT_VALUE }),
                notADomains: translate(ENR_NOT_A_DOMAINS_ERROR),
                columnsNumber: translate(ENR_COL_NUMBER_ERROR, { quota: formattedQuota }),
                format: translate(ENR_FORMAT_ERROR),
            };
        } else {
            return {
                isOverTheLimit: translate(ENR_CONTACTS_OVER_LIMIT_ERROR, { limit: MAX_CONTACTS_ROW_NUMBER }),
                quotaExceeded: translate(ENR_CONTACTS_QUOTA_ERROR, { quota: formattedQuota }),
                noRequiredFields: translate(ENR_CONTACTS_NO_FIELDS_ERROR),
                emptyColumns: translate(ENR_CONTACTS_EMPTY_COLUMNS_ERROR),
                format: translate(ENR_FORMAT_ERROR),
            };
        }
    }, [type, formattedQuota]);

    const handleCloseModal = (): void => {
        enrichmentTrackingService.csv.trackCancelUpload(type);
        onClose?.();
    };

    return (
        <StyledUploadContainer>
            <Header
                tooltipContent={translate(CLOSE)}
                onClick={handleCloseModal}
                title={type === "company" ? ENR_MODAL_TITLE : ENR_CONTACTS_MODAL_TITLE}
                iconName="close"
                iconSize="xs"
            />
            {type === "company" ? (
                <UploadCSV
                    handleFileChange={onFileChange}
                    errorTexts={errorTexts}
                    hasCloseBtn={false}
                    onClose={onClose}
                    dataAutomation="enrichment-upload-csv-file-input"
                    isLoading={isParsing}
                    className="enrichment"
                    hasParsingError={hasParsingError}
                    parsingErrors={parsingError}
                    title={null}
                    isReplace={false}
                    customInstructions={<UploadEnrichmentInstructions type={type} />}
                    customHint={
                        !isDataCreditsEnabled ? (
                            <CustomHint
                                type={type}
                                quota={formattedQuota}
                            />
                        ) : undefined
                    }
                    hintWidth={HINT_WIDTH}
                    type={type}
                    hasCustomTracking
                />
            ) : (
                <UploadContactsCSV
                    handleFileChange={onFileChange}
                    errorTexts={errorTexts}
                    dataAutomation="enrichment-upload-csv-file-input"
                    isLoading={isParsing}
                    hasParsingError={hasParsingError}
                    parsingErrors={parsingError}
                    customHint={
                        !isDataCreditsEnabled ? (
                            <CustomHint
                                type={type}
                                quota={formattedQuota}
                            />
                        ) : undefined
                    }
                    hintWidth={HINT_WIDTH}
                    type={type}
                    hasCustomTracking
                />
            )}
        </StyledUploadContainer>
    );
};

export default UploadCSVEnrichmentContainer;
